import React, { useState, useEffect } from 'react';
import styles from './Sidebar.module.scss';
import SVG from 'react-inlinesvg';
import classes from 'classnames';
import chat from '@/admin/assets/svg/chat.svg';
import MainMenu from '../MainMenu/MainMenu';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import clsx from 'clsx';
import { useDrawer } from '@/admin/context/DrawerContext';
import { useAppSelector } from '@/store';
import { authSelector, interfaceLanguageSelector } from '@/store/reducers/auth';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import logo from '@/assets/svg/logo_new.svg';
import logo60x60 from '@/assets/svg/logo_60x60.svg';
import whiteArrow from '@/admin/assets/svg/whiteArrow.svg';

interface SidebarProps {
  isCollapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onToggleCollapse, isCollapsed = false }) => {
  const { user } = useAppSelector(authSelector);
  const [logoIcon, setLogoIcon] = useState('logoIcon');
  const { t } = useAdminTranslation();
  const interfaceLanguage = useAppSelector(interfaceLanguageSelector);
  const nativeLanguage = user?.metadata.languagePreferences.nativeLanguage;
  const isInterfaceLangHE = interfaceLanguage?.code === 'he';
  const drawer = useDrawer();

  const isNativeLanguageHEAndInterfaceEN =
    nativeLanguage === 'he' && interfaceLanguage?.code === 'en';
  const isRtl = interfaceLanguage?.direction === 'rtl';

  // Handle logo icon change with delay for smooth animation
  useEffect(() => {
    let timeoutId: any;

    if (isCollapsed) {
      // When collapsing, change icon after half the animation
      timeoutId = setTimeout(() => {
        setLogoIcon('logo');
      }, 150); // Half of 300ms transition
    } else {
      // When expanding, change icon immediately
      setLogoIcon('logoIcon');
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isCollapsed]);

  return (
    <aside className={clsx(styles.sidebar, { [styles.collapsed]: isCollapsed })}>
      <div className={styles.header}>
        <img
          src={isCollapsed ? logo60x60 : logo}
          alt='Hello Britannica'
          className={clsx(styles.logo, { [styles.collapsed]: isCollapsed })}
        />

        <button
          className={clsx(styles.collapseButton, { [styles.collapsed]: isCollapsed })}
          onClick={() => onToggleCollapse?.(!isCollapsed)}
          aria-label={isCollapsed ? t('expandSidebar') || '' : t('collapseSidebar') || ''}
        >
          <AppIcon icon='expand' className={clsx(styles.collapseIcon)} />
        </button>
      </div>

      <div className={styles.menuSection}>
        <MainMenu isCollapsed={isCollapsed} />
      </div>
      <div className={styles.help}>
        <img
          className={classes(styles.chat, {
            [styles.chatSvgRtl]: isRtl,
          })}
          src={chat}
        />
        <div className={styles.helpContent}>
          <h2>{t('needHelp')}</h2>
          <p>
            {isNativeLanguageHEAndInterfaceEN ? (
              <span>{t('contactOurTeamWithQuestions')}</span>
            ) : (
              <>
                <span>{t('contactOurTeam')}</span>
                <span>{t('britannicaEducationWith')}</span>
                {!isInterfaceLangHE && <span>{t('questionsAndConcerns')}</span>}
              </>
            )}
          </p>
          <button className={styles.feedbackBtn} onClick={drawer.openContactUsDrawer}>
            {t('giveFeedback')}
            <SVG
              className={classes(styles.arrow, {
                [styles.arrowSvgRtl]: isRtl,
              })}
              src={whiteArrow}
            />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
